/**
 * Carousel component with autoplay toggle.
 * @module components/Carousel/Carousel
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { DotButton, useDotButton } from './CarouselDotButtons';
import {
  PrevButton,
  NextButton,
  usePrevNextButtons,
} from './CarouselArrowButtons';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay } from '@fortawesome/free-solid-svg-icons';
import { faCirclePause } from '@fortawesome/free-regular-svg-icons';

const messages = defineMessages({
  slideshowNav: {
    id: 'Slideshow navigation',
    defaultMessage: 'Slideshow navigation',
  }
});

const Carousel = (props) => {
  const { slides, ItemView, options } = props;
  const intl = useIntl();

  const [autoplayStopped, setAutoplayStopped] = useState(false);
  const autoplay = options?.autoplay
    ? Autoplay({
      delay: options?.delayTime || 4000,
      stopOnMouseEnter: options?.stopOnMouseEnter ?? true,
    })
    : undefined;
  const buttons = options?.buttons ? options?.buttons : false
  const dots = options?.dots ? options?.dots : false
  const numberDots = options?.numberDots ? options?.numberDots : false

  const [emblaRef, emblaApi] = useEmblaCarousel(
    options,
    autoplay ? [autoplay] : []
  );

  const toggleAutoplay = () => {
    if (!emblaApi) return;
    const plugin = emblaApi.plugins()?.autoplay;
    if (!plugin) return;

    setAutoplayStopped(prev => {
      const next = !prev;
      next ? plugin.stop() : plugin.play();
      return next;
    });
  };

  const onButtonClick = useCallback((emblaApi) => {
    if (!emblaApi || typeof emblaApi.plugins !== 'function') return;

    const { autoplay } = emblaApi.plugins();
    if (!autoplay) return;
    if (autoplay.options.stopOnInteraction !== false) autoplay.stop();
  }, []);

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(
    emblaApi,
    onButtonClick
  );

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi, onButtonClick);

  return (
    <div className={`${options?.customClass} slider-wrapper`}>
      <div className="slider__viewport" ref={emblaRef}>
        <div className="slider__container">
          {slides.map((item, index) => (
            <div className="slider__slide" key={item?.['@id'] || index}>
              <ItemView item={item} />
            </div>
          ))}
        </div>
      </div>

      <div className="slideControls gap-2  flex-wrap">
        <div className="slideControls__dots__wrapper d-flex gap-2 flex-wrap">
          {options?.dots && (
            <nav
              className="slider__dots"
              aria-label={intl.formatMessage(messages.slideshowNav)}
            >
              {scrollSnaps.map((_, index) => (
                <DotButton
                  key={index}
                  onClick={() => onDotButtonClick(index)}
                  className={'slider__dot'.concat(
                    index === selectedIndex ? ' slider__dot--selected' : ''
                  )}
                >
                  <span className="visually-hidden">
                    <FormattedMessage id="Slide" defaultMessage="Slide" /> {index + 1}
                  </span>
                </DotButton>
              ))}
            </nav>
          )}
        </div>

        {options?.autoplay && (
          <button
            type="button"
            onClick={toggleAutoplay}
            aria-pressed={autoplayStopped ? 'true' : 'false'}
            className="slider__autoplay-toggle flex-shrink-0"
          >
            {autoplayStopped ? (
              <>
                <span className="visually-hidden"><FormattedMessage id="Play" defaultMessage="Play" /></span>
                <FontAwesomeIcon icon={faPlay} />
              </>
            ) : (
              <>
                <span className="visually-hidden"><FormattedMessage id="Pause" defaultMessage="Pause" /></span>
                <FontAwesomeIcon icon={faCirclePause} />
              </>
            )}

          </button>
        )}

        {options?.buttons && (
          <div className="flex-shrink-0 d-flex gap-2">
            <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} />
            <NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} />
          </div>
        )}
      </div>
    </div>
  );
};

export default Carousel;
